import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Payments, Terminal } from '@mui/icons-material';
import * as admin from '@/api/admin';
import { toApiError } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/pagos/')({
  component: AdminPaymentsPage,
});

function statusColor(status: number): 'success' | 'warning' | 'error' | 'default' {
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  if (status >= 200 && status < 400) return 'success';
  return 'default';
}

function AdminPaymentsPage(): JSX.Element {
  const [tab, setTab] = useState<'payments' | 'api'>('payments');
  const [tenantId, setTenantId] = useState('');
  const [provider, setProvider] = useState('');
  const [kind, setKind] = useState('');
  const [method, setMethod] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [status, setStatus] = useState('');

  const paymentsQ = useQuery({
    queryKey: ['admin-payment-audit', tenantId, provider, kind],
    queryFn: () => admin.listPaymentAudit({
      tenantId: tenantId || undefined,
      provider: provider || undefined,
      kind: kind || undefined,
      take: 100,
    }),
    enabled: tab === 'payments',
  });
  const apiQ = useQuery({
    queryKey: ['admin-platform-api-logs', tenantId, method, endpoint, status],
    queryFn: () => admin.listPlatformApiLogs({
      tenantId: tenantId || undefined,
      method: method || undefined,
      endpoint: endpoint || undefined,
      status: status ? Number(status) : undefined,
      take: 100,
    }),
    enabled: tab === 'api',
  });

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Payments color="primary" /> Tilde Pagos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Operaciones de pago y llamadas de clientes, con correlation ID y estado para soporte y medición comercial.
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Panadería Tacuarí hoy ejecuta Tilde Pagos embebido: sus pagos se registran en su propia base, pero no aparecen
        en este ledger central hasta migrar a la API hosted o enviar eventos de uso a Tilde. La facturación AFIP sí
        pasa por la API de Tilde y aparece en “Llamadas API”.
      </Alert>

      <Tabs value={tab} onChange={(_, value: 'payments' | 'api') => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="payments" label="Auditoría de pagos" icon={<Payments fontSize="small" />} iconPosition="start" />
        <Tab value="api" label="Llamadas API" icon={<Terminal fontSize="small" />} iconPosition="start" />
      </Tabs>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" label="Tenant ID" value={tenantId} onChange={(e) => setTenantId(e.target.value)} sx={{ minWidth: 250 }} />
        {tab === 'payments' ? (
          <>
            <TextField size="small" label="Proveedor" value={provider} onChange={(e) => setProvider(e.target.value)} sx={{ minWidth: 160 }} />
            <Select size="small" displayEmpty value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">Todos los eventos</MenuItem>
              <MenuItem value="Request">Request</MenuItem>
              <MenuItem value="Response">Response</MenuItem>
              <MenuItem value="Exception">Exception</MenuItem>
            </Select>
          </>
        ) : (
          <>
            <Select size="small" displayEmpty value={method} onChange={(e) => setMethod(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">Todos los métodos</MenuItem>
              {['GET', 'POST', 'PUT', 'DELETE'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
            <TextField size="small" label="Endpoint contiene" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} sx={{ minWidth: 230 }} />
            <Select size="small" displayEmpty value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">Todos los estados</MenuItem>
              <MenuItem value="200">200</MenuItem>
              <MenuItem value="401">401</MenuItem>
              <MenuItem value="403">403</MenuItem>
              <MenuItem value="500">500</MenuItem>
            </Select>
          </>
        )}
      </Stack>

      {tab === 'payments' ? <PaymentAuditTable query={paymentsQ} /> : <ApiLogTable query={apiQ} />}
    </Box>
  );
}

function PaymentAuditTable({ query }: { query: ReturnType<typeof useQuery<admin.PaymentAuditQueryResponse>> }): JSX.Element {
  if (query.isLoading) return <LoadingScreen message="Cargando auditoría de pagos…" />;
  if (query.error) return <Alert severity="error">{toApiError(query.error).message}</Alert>;
  const rows = query.data?.items ?? [];
  if (rows.length === 0) return <EmptyState icon={<Payments fontSize="inherit" />} title="Sin operaciones de pago" description="No hay auditoría central para este filtro." />;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Fecha</TableCell><TableCell>Tenant</TableCell><TableCell>Proveedor</TableCell>
          <TableCell>Evento</TableCell><TableCell>Operación</TableCell><TableCell>Referencia</TableCell><TableCell>Detalle</TableCell>
        </TableRow></TableHead>
        <TableBody>{rows.map((row) => (
          <TableRow key={row.id} hover>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.occurredAtUtc)}</TableCell>
            <TableCell><Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>{row.tenantId ?? '—'}</Typography></TableCell>
            <TableCell><Chip size="small" variant="outlined" label={row.providerKey} /></TableCell>
            <TableCell>{row.kind}</TableCell><TableCell>{row.operationKind}</TableCell>
            <TableCell>{row.externalReference ?? row.providerRef ?? '—'}</TableCell>
            <TableCell><Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>{row.data.length > 100 ? `${row.data.slice(0, 100)}…` : row.data}</Typography></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </TableContainer>
  );
}

function ApiLogTable({ query }: { query: ReturnType<typeof useQuery<admin.PlatformApiLogQueryResponse>> }): JSX.Element {
  if (query.isLoading) return <LoadingScreen message="Cargando llamadas de API…" />;
  if (query.error) return <Alert severity="error">{toApiError(query.error).message}</Alert>;
  const rows = query.data?.items ?? [];
  if (rows.length === 0) return <EmptyState icon={<Terminal fontSize="inherit" />} title="Sin llamadas registradas" description="No hay requests de Invoicing para este filtro." />;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Fecha</TableCell><TableCell>Tenant</TableCell><TableCell>Método</TableCell>
          <TableCell>Endpoint</TableCell><TableCell>Estado</TableCell><TableCell>Latencia</TableCell><TableCell>Client / correlation</TableCell>
        </TableRow></TableHead>
        <TableBody>{rows.map((row) => (
          <TableRow key={row.id} hover>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.occurredAt)}</TableCell>
            <TableCell><Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>{row.tenantId.slice(0, 12)}…</Typography></TableCell>
            <TableCell><Chip size="small" variant="outlined" label={row.method} /></TableCell>
            <TableCell><Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)' }}>{row.endpoint}</Typography></TableCell>
            <TableCell><Chip size="small" color={statusColor(row.statusCode)} variant="outlined" label={row.statusCode} /></TableCell>
            <TableCell>{row.latencyMs} ms</TableCell>
            <TableCell><Typography variant="caption">{row.clientId ?? '—'} · {row.correlationId.slice(0, 8)}…</Typography></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </TableContainer>
  );
}
